import { useEffect } from "react";
import { Card, Pill } from "./ui";
import { Ic } from "./icons";

/**
 * Variante du toast affiché après un retour de Stripe Checkout.
 * - `subscription-success` : nouvel abonnement actif → "votre plan est activé"
 * - `topup-success`        : crédits add-on (qty connu) → "X fiches créditées"
 * - `pack-success`         : pack one-time (qty connu via packId) → idem
 * - `cancel`               : checkout abandonné, peu importe le kind
 */
export type CheckoutToastKind =
  | "subscription-success"
  | "topup-success"
  | "pack-success"
  | "cancel";

export type CheckoutToastProps = {
  kind: CheckoutToastKind;
  /** Quantité associée à l'achat (topup ou pack). Affichée dans la copy. */
  quantity?: number;
  onClose: () => void;
};

/**
 * Bannière flottante affichée après un retour de Stripe Checkout. Non-modale
 * (l'utilisateur peut continuer à naviguer), auto-dismiss après 8s. Rendue
 * en portal-less (fixed) depuis App.tsx pour apparaître au-dessus de
 * n'importe quelle page.
 *
 * Les side-effects de facturation (maj plan, crédits) sont déjà persistés
 * par le webhook Stripe au moment où l'user atterrit ici — on se contente
 * de communiquer. Le onSnapshot sur /billing/current (via useEntitlements)
 * refresh l'UI en temps réel, donc pas besoin de forcer un reload.
 */
export function CheckoutToast({
  kind,
  quantity,
  onClose,
}: CheckoutToastProps) {
  useEffect(() => {
    const id = window.setTimeout(onClose, 8000);
    return () => window.clearTimeout(id);
  }, [onClose]);

  const isSuccess = kind !== "cancel";
  const copy = getCopy(kind, quantity);

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-20 right-4 sm:top-24 sm:right-6 z-[210] max-w-[380px] page-enter"
    >
      <Card className="p-4 sm:p-5 shadow-pop hairline">
        <div className="flex items-start gap-3">
          <div
            className={`w-9 h-9 rounded-xl2 grid place-items-center shrink-0 ${
              isSuccess ? "bg-lime-300 text-ink-900" : "bg-ink-100 text-ink-700"
            }`}
          >
            {isSuccess ? <Ic.Check size={16} /> : <Ic.Info size={16} />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Pill tone={isSuccess ? "lime" : "soft"}>{copy.pill}</Pill>
            </div>
            <h3 className="mt-2 text-[14.5px] font-semibold tracking-tight">
              {copy.title}
            </h3>
            <p className="mt-1 text-[12.5px] text-ink-600 leading-relaxed">
              {copy.body}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Fermer"
            className="w-7 h-7 -mt-1 -mr-1 rounded-full hover:bg-ink-100 grid place-items-center text-ink-500 shrink-0"
          >
            <Ic.Cross size={13} />
          </button>
        </div>
      </Card>
    </div>
  );
}

function getCopy(
  kind: CheckoutToastKind,
  quantity?: number,
): { pill: string; title: string; body: string } {
  if (kind === "cancel") {
    return {
      pill: "Paiement annulé",
      title: "Vous pouvez réessayer à tout moment",
      body: "Aucun montant n'a été débité. Votre panier reste disponible si vous souhaitez terminer plus tard.",
    };
  }
  if (kind === "subscription-success") {
    return {
      pill: "Paiement confirmé",
      title: "Votre plan est activé",
      body: "Vos crédits sont disponibles immédiatement. Retrouvez votre abonnement dans Paramètres → Abonnement.",
    };
  }
  if (kind === "topup-success") {
    const n = quantity ?? 0;
    return {
      pill: "Paiement confirmé",
      title:
        n > 0
          ? `${n} fiche${n > 1 ? "s" : ""} add-on créditée${n > 1 ? "s" : ""}`
          : "Crédits add-on ajoutés",
      body: "Vos crédits sont disponibles immédiatement. Détails dans Paramètres → Abonnement → Crédits add-on.",
    };
  }
  // pack-success
  const n = quantity ?? 0;
  return {
    pill: "Paiement confirmé",
    title:
      n > 0
        ? `${n} fiche${n > 1 ? "s" : ""} créditée${n > 1 ? "s" : ""}`
        : "Pack activé",
    body: "Vos crédits sont disponibles immédiatement. Détails dans Paramètres → Abonnement → Crédits add-on.",
  };
}
